import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import {
  AiControllerV4,
  delphiResponseEventDef,
  HandlePromptDelphiParams,
} from 'features/cantileverAISystem/components/aiControllerV4'
import { AIPromptEvent } from 'features/cantileverAISystem/components/makeAiPromptEventDef'
import { useGeckoPermissions } from 'features/permissions'
import { useEffect } from 'react'
import z from 'zod'

import { AiControllerV3 } from './components/aiControllerV3'
import { EventBus, EventDefinition, EventHandler } from './components/eventBus'

const eventBus = new EventBus()
const aiController = new AiControllerV3(eventBus)
const aiControllerV4 = new AiControllerV4(eventBus)
eventBus.subscribeAllAIPromptEvent(
  aiController.deprecated_handleAIPromptEvent.bind(aiController)
)

export function useAIEvents() {
  const { authorization } = useGeckoAuth()
  const { user } = useGeckoPermissions()
  const { LLM_SERVICE } = useRuntimeVariables()

  useEffect(() => {
    aiController.setConfig({
      authorization,
      LLM_SERVICE,
      userId: user?.id,
    })

    aiControllerV4.setConfig({
      authorization,
      LLM_SERVICE,
      userId: user?.id,
    })
  }, [authorization, LLM_SERVICE, user?.id])

  const useSubscribe = <TEventDef extends EventDefinition>(
    eventDef: TEventDef,
    handler: EventHandler<z.infer<TEventDef['eventSchema']>>
  ) => {
    useEffect(() => {
      eventBus.subscribe(eventDef, handler)
      return () => {
        eventBus.unsubscribe(eventDef.eventName, handler)
      }
    }, [eventDef, handler])
  }

  const promptDelphi = (params: HandlePromptDelphiParams) => {
    aiControllerV4.handlePromptDelphi(params)
  }

  const promptDelphiV3 = async (params: {
    prompt: string
    promptEvent: AIPromptEvent
    systemPrompt?: string
    sessionId?: string
  }) => {
    return aiController.sendDelphiQuery(params)
  }

  const useOnDelphiResponse = (
    handler: EventHandler<
      z.infer<(typeof delphiResponseEventDef)['eventSchema']>
    >
  ) => {
    useSubscribe(delphiResponseEventDef, handler)
  }

  return {
    useSubscribe,
    unsubscribe: eventBus.unsubscribe.bind(eventBus),
    deprecated_send: eventBus.send.bind(eventBus),
    promptDelphi,
    promptDelphiV3,
    useOnDelphiResponse,
  }
}
